import { useEffect, useState } from "react";
import { createSiteSettings, getSiteSettings, updateSiteSettings } from "../../../api/apiServices";
import { DEFAULT_SETTINGS } from "../../../mocks/dashboardData";



export default function SiteSettingsSection() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoPreview, setLogoPreview] = useState(null);
const [faviconPreview, setFaviconPreview] = useState(null);

  /* ---------------- FETCH SETTINGS ---------------- */
  useEffect(() => {
  const fetchSettings = async () => {
    try {
      const res = await getSiteSettings();

      if (res?.data) {
        setSettings({
          ...DEFAULT_SETTINGS,
          ...res.data,
          socialLinks: {
            ...DEFAULT_SETTINGS.socialLinks,
            ...res.data.socialLinks,
          },
        });
        setExists(true);
      }
    } catch (err) {
      // 404 = first-time setup
      console.log("No site settings yet, using defaults");
      setSettings(DEFAULT_SETTINGS);
      setExists(false);
    } finally {
      setLoading(false);
    }
  };

  fetchSettings();
}, []);


  if (loading) {
  return (
    <div className="p-6 text-gray-500">
      Loading site settings…
    </div>
  );
}

const handleLogoChange = (file) => {
  setLogoFile(file);
  if (file) {
    setLogoPreview(URL.createObjectURL(file));
  }
};

const handleFaviconChange = (file) => {
  setFaviconFile(file);
  if (file) {
    setFaviconPreview(URL.createObjectURL(file));
  }
};


  /* ---------------- SAVE HANDLER ---------------- */
  const handleSave = async () => {
    setIsSaving(true);

    try {
      const formData = new FormData();

      formData.append("siteName", settings.siteName);
      formData.append("shortDescription", settings.shortDescription);
      formData.append("mapUrl", settings.mapUrl || "");
      formData.append("isActive", settings.isActive);

      formData.append("meta", JSON.stringify(settings.meta || {}));
      formData.append(
        "socialLinks",
        JSON.stringify(settings.socialLinks || {})
      );

      if (logoFile) formData.append("logo", logoFile);
      if (faviconFile) formData.append("favicon", faviconFile);

      if (exists) {
        await updateSiteSettings(formData);
      } else {
        await createSiteSettings(formData);
        setExists(true);
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
  <div className="space-y-8">
    {/* ================= HEADER ================= */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Site Settings</h2>
        <p className="text-sm text-gray-500">
          Configure your website's basic information
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="inline-flex items-center gap-2 bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#5a3ba3] transition disabled:opacity-50"
      >
        {isSaving ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            Saving…
          </>
        ) : (
          <>
            <i className="ri-save-line"></i>
            Save Changes
          </>
        )}
      </button>
    </div>

    {showSuccess && (
      <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
        <i className="ri-check-line"></i>
        Settings saved successfully
      </div>
    )}

    {/* ================= SITE STATUS ================= */}
    <section className="bg-white rounded-xl border shadow-sm">
      <div className="border-b px-6 py-4">
        <h3 className="font-semibold text-gray-900">Site Status</h3>
      </div>

      <div className="p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">
            Website settings Active
          </p>
          <p className="text-xs text-gray-500">
            Disable website settings temporarily 
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            setSettings({ ...settings, isActive: !settings.isActive })
          }
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
            settings.isActive ? "bg-[#6a4bc3]" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              settings.isActive ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </section>

    {/* ================= BRANDING ================= */}
    <section className="bg-white rounded-xl border shadow-sm">
      <div className="border-b px-6 py-4">
        <h3 className="font-semibold text-gray-900">Logo & Branding</h3>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-6">
        {/* Logo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo
          </label>

          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden">
              {logoPreview || settings.logo ? (
                <img
                  src={logoPreview || settings.logo}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <i className="ri-image-line text-gray-400 text-2xl"></i>
              )}
            </div>

            <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-[#6a4bc3] font-medium">
              <i className="ri-upload-line"></i>
              Upload logo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleLogoChange(e.target.files[0])}
              />
            </label>
          </div>
        </div>

        {/* Favicon */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Favicon
          </label>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded border bg-gray-50 flex items-center justify-center overflow-hidden">
              {faviconPreview || settings.favicon ? (
                <img
                  src={faviconPreview || settings.favicon}
                  alt="Favicon"
                  className="w-full h-full object-contain"
                />
              ) : (
                <i className="ri-star-line text-gray-400"></i>
              )}
            </div>

            <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-[#6a4bc3] font-medium">
              <i className="ri-upload-line"></i>
              Upload favicon
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFaviconChange(e.target.files[0])}
              />
            </label>
          </div>
        </div>
      </div>
    </section>

    {/* ================= SITE INFO ================= */}
    <section className="bg-white rounded-xl border shadow-sm">
      <div className="border-b px-6 py-4">
        <h3 className="font-semibold text-gray-900">Site Information</h3>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-6">
        <div>
          <label className="label">Site Name</label>
          <input
            type="text"
            value={settings.siteName}
            onChange={(e) =>
              setSettings({ ...settings, siteName: e.target.value })
            }
            className="input"
          />
        </div>

        <div>
          <label className="label">Short Description</label>
          <input
            type="text"
            value={settings.shortDescription}
            onChange={(e) =>
              setSettings({
                ...settings,
                shortDescription: e.target.value,
              })
            }
            className="input"
          />
        </div>
      </div>
    </section>

    {/* ================= SOCIAL LINKS ================= */}
    <section className="bg-white rounded-xl border shadow-sm">
      <div className="border-b px-6 py-4">
        <h3 className="font-semibold text-gray-900">Social Links</h3>
      </div>

      <div className="p-6 space-y-4">
        {["facebook", "twitter", "instagram", "linkedin"].map((key) => (
          <div key={key}>
            <label className="label capitalize">{key}</label>
            <input
              type="url"
              placeholder={`https://${key}.com/...`}
              value={settings.socialLinks[key] || ""}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  socialLinks: {
                    ...settings.socialLinks,
                    [key]: e.target.value,
                  },
                })
              }
              className="input"
            />
          </div>
        ))}
      </div>
    </section>
  </div>
);


}
